import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  TWITTER_HANDLE,
  absoluteUrl,
} from '../utils/seoConfig';

/**
 * Reusable SEO component. Drop one of these at the top of any page that
 * should have a unique title, description, canonical URL, OpenGraph card,
 * Twitter card, or Schema.org JSON-LD payload.
 *
 * Props:
 *  - title           page-specific title (will be appended with site name)
 *  - description     page-specific description
 *  - canonical       absolute or relative URL of the canonical page
 *  - image           OpenGraph/Twitter image URL
 *  - type            OpenGraph type (default 'website')
 *  - keywords        array of keyword strings (modest impact, but harmless)
 *  - robots          override the robots meta value (default 'index,follow')
 *  - jsonLd          single object or array of Schema.org JSON-LD objects
 *  - noTitleSuffix   if true, render the title verbatim without "| FreshFitLab"
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  keywords,
  robots = 'index,follow',
  jsonLd,
  noTitleSuffix = false,
}) => {
  const finalTitle = !title
    ? DEFAULT_TITLE
    : noTitleSuffix || title.includes(SITE_NAME)
    ? title
    : `${title} | ${SITE_NAME}`;

  const finalCanonical = canonical
    ? absoluteUrl(canonical)
    : typeof window !== 'undefined'
    ? `${SITE_URL}${window.location.pathname}`
    : SITE_URL;

  const finalImage = image && !/^https?:\/\//.test(image) ? absoluteUrl(image) : image;

  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={finalCanonical} />

      {/* OpenGraph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="600" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalImage} />

      {jsonLdArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
